/**
 * StaffTab — Admin CRUD for staff directory with sections.
 */
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Trash2, Edit, Check, X, Loader2, Eye, EyeOff, FolderPlus, GripVertical } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API, getAuthHeader } from "./utils";
import ImageUpload from "./ImageUpload";

/* ── Section Form ── */
const SectionForm = ({ initial, onSave, onCancel }) => {
  const [name, setName] = useState(initial?.name || "");
  const [order, setOrder] = useState(initial?.order ?? 0);
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try { await onSave({ name, order }); } finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="flex items-center gap-2 bg-zinc-900/60 border border-white/10 p-3" data-testid="section-form">
      <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Section name (e.g. On-Air Talent)"
        className="flex-1 bg-black/40 border border-white/10 text-white px-3 py-1.5 text-sm" data-testid="section-name-input" autoFocus />
      <input type="number" value={order} onChange={(e) => setOrder(parseInt(e.target.value) || 0)} title="Sort order"
        className="w-16 bg-black/40 border border-white/10 text-white px-2 py-1.5 text-xs text-center" />
      <button type="submit" disabled={saving} className="bg-white text-black px-3 py-1.5 text-xs font-bold hover:bg-zinc-200" data-testid="save-section-btn">
        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
      </button>
      {onCancel && <button type="button" onClick={onCancel} className="text-zinc-500 hover:text-white p-1"><X className="w-3.5 h-3.5" /></button>}
    </form>
  );
};

/* ── Staff Form ── */
const emptyForm = { name: "", role: "", photo_url: "", bio: "", years_with_company: "", section_id: "", order: 0, is_visible: true };

const StaffForm = ({ initial, sections, onSave, onCancel }) => {
  const [form, setForm] = useState(initial ? { ...initial, years_with_company: initial.years_with_company ?? "" } : { ...emptyForm });
  const [saving, setSaving] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      await onSave({ ...form, years_with_company: form.years_with_company === "" ? null : parseInt(form.years_with_company) || null });
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-zinc-900/60 border border-white/10 p-5 space-y-4" data-testid="staff-form">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Name</label>
          <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full bg-black/40 border border-white/10 text-white px-3 py-2 text-sm" placeholder="Jane Doe" data-testid="staff-name-input" />
        </div>
        <div>
          <label className="block text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Role / Title</label>
          <input type="text" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
            className="w-full bg-black/40 border border-white/10 text-white px-3 py-2 text-sm" placeholder="Play-by-Play Announcer" data-testid="staff-role-input" />
        </div>
        <div>
          <label className="block text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Section</label>
          <select value={form.section_id} onChange={(e) => setForm({ ...form, section_id: e.target.value })}
            className="w-full bg-black/40 border border-white/10 text-white px-3 py-2 text-sm" data-testid="staff-section-select">
            <option value="">— No Section —</option>
            {sections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Photo</label>
        <ImageUpload value={form.photo_url} onChange={(url) => setForm({ ...form, photo_url: url })} label="Photo" type="logo" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Years with Company</label>
          <input type="number" value={form.years_with_company} onChange={(e) => setForm({ ...form, years_with_company: e.target.value })}
            className="w-40 bg-black/40 border border-white/10 text-white px-3 py-2 text-sm" placeholder="e.g. 3" min={0} data-testid="staff-years-input" />
        </div>
        <div className="flex items-end gap-4 pb-1">
          <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
            <input type="checkbox" checked={form.is_visible} onChange={(e) => setForm({ ...form, is_visible: e.target.checked })} className="accent-orange-500" />
            Visible on site
          </label>
          <div className="flex items-center gap-2">
            <label className="text-[10px] text-zinc-500 uppercase">Order</label>
            <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })}
              className="w-16 bg-black/40 border border-white/10 text-white px-2 py-1 text-xs text-center" />
          </div>
        </div>
      </div>
      <div>
        <label className="block text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Bio</label>
        <textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })}
          className="w-full bg-black/40 border border-white/10 text-white px-3 py-2 text-sm h-24" placeholder="Brief bio..." data-testid="staff-bio-input" />
      </div>
      <div className="flex gap-2 pt-2">
        <button type="submit" disabled={saving}
          className="flex items-center gap-2 bg-white text-black px-5 py-2 text-sm font-bold hover:bg-zinc-200 transition-colors" data-testid="staff-save-btn">
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : initial ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
          {initial ? "Save Changes" : "Add Staff"}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="flex items-center gap-2 text-zinc-500 hover:text-white px-4 py-2 text-sm transition-colors">
            <X className="w-3 h-3" /> Cancel
          </button>
        )}
      </div>
    </form>
  );
};

/* ── Main Tab ── */
export const StaffTab = () => {
  const [staff, setStaff] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showCreateSection, setShowCreateSection] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingSectionId, setEditingSectionId] = useState(null);

  const fetchAll = async () => {
    try {
      const [staffRes, sectionsRes] = await Promise.all([
        axios.get(`${API}/staff`, { headers: getAuthHeader() }),
        axios.get(`${API}/staff/sections`, { headers: getAuthHeader() }),
      ]);
      setStaff(staffRes.data || []);
      setSections(sectionsRes.data || []);
    } catch { }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  // Section handlers
  const handleCreateSection = async (form) => {
    try {
      await axios.post(`${API}/staff/sections`, form, { headers: getAuthHeader() });
      toast.success("Section created");
      setShowCreateSection(false);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const handleUpdateSection = async (id, form) => {
    try {
      await axios.put(`${API}/staff/sections/${id}`, form, { headers: getAuthHeader() });
      toast.success("Section updated");
      setEditingSectionId(null);
      fetchAll();
    } catch { toast.error("Failed to update"); }
  };
  const handleDeleteSection = async (section) => {
    if (!window.confirm(`Delete section "${section.name}"? Staff in it will become unsectioned.`)) return;
    try {
      await axios.delete(`${API}/staff/sections/${section.id}`, { headers: getAuthHeader() });
      toast.success("Section deleted");
      fetchAll();
    } catch { toast.error("Failed to delete"); }
  };

  // Staff handlers
  const handleCreate = async (form) => {
    try {
      await axios.post(`${API}/staff`, form, { headers: getAuthHeader() });
      toast.success("Staff member added");
      setShowCreate(false);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const handleUpdate = async (id, form) => {
    try {
      await axios.put(`${API}/staff/${id}`, form, { headers: getAuthHeader() });
      toast.success("Updated");
      setEditingId(null);
      fetchAll();
    } catch { toast.error("Failed to update"); }
  };
  const handleDelete = async (member) => {
    if (!window.confirm(`Delete ${member.name}?`)) return;
    try {
      await axios.delete(`${API}/staff/${member.id}`, { headers: getAuthHeader() });
      toast.success("Deleted");
      fetchAll();
    } catch { toast.error("Failed to delete"); }
  };
  const toggleVisibility = async (member) => {
    try {
      await axios.put(`${API}/staff/${member.id}`, { is_visible: !member.is_visible }, { headers: getAuthHeader() });
      fetchAll();
    } catch { toast.error("Failed to toggle"); }
  };

  const getSectionName = (id) => sections.find(s => s.id === id)?.name || "—";

  if (loading) return <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin text-zinc-600 mx-auto" /></div>;

  return (
    <div className="space-y-6" data-testid="staff-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-white text-lg font-bold">Staff Directory</h2>
          <p className="text-zinc-500 text-xs mt-1">Manage sections and staff members shown on the public page.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { setShowCreateSection(!showCreateSection); setShowCreate(false); }}
            className="flex items-center gap-2 bg-zinc-800 text-white border border-white/10 px-4 py-2 text-sm font-bold hover:bg-zinc-700 transition-colors" data-testid="add-section-btn">
            <FolderPlus className="w-4 h-4" /> Add Section
          </button>
          <button onClick={() => { setShowCreate(!showCreate); setShowCreateSection(false); setEditingId(null); }}
            className="flex items-center gap-2 bg-white text-black px-4 py-2 text-sm font-bold hover:bg-zinc-200 transition-colors" data-testid="add-staff-btn">
            <Plus className="w-4 h-4" /> Add Staff
          </button>
        </div>
      </div>

      {/* Section form */}
      <AnimatePresence>{showCreateSection && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
          <SectionForm onSave={handleCreateSection} onCancel={() => setShowCreateSection(false)} />
        </motion.div>
      )}</AnimatePresence>

      {/* Staff form */}
      <AnimatePresence>{showCreate && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
          <StaffForm sections={sections} onSave={handleCreate} onCancel={() => setShowCreate(false)} />
        </motion.div>
      )}</AnimatePresence>

      {/* Sections list */}
      {sections.length > 0 && (
        <div className="space-y-1">
          <h3 className="text-[10px] text-zinc-500 uppercase tracking-widest font-bold">Sections</h3>
          {sections.map((s) => (
            <div key={s.id}>
              {editingSectionId === s.id ? (
                <SectionForm initial={s} onSave={(form) => handleUpdateSection(s.id, form)} onCancel={() => setEditingSectionId(null)} />
              ) : (
                <div className="flex items-center gap-3 bg-zinc-900/40 border border-white/5 px-4 py-2 group" data-testid={`section-card-${s.id}`}>
                  <GripVertical className="w-3 h-3 text-zinc-700" />
                  <span className="text-white text-sm font-bold flex-1">{s.name}</span>
                  <span className="text-zinc-600 text-[10px]">{staff.filter(m => m.section_id === s.id).length} members</span>
                  <button onClick={() => setEditingSectionId(s.id)} className="p-1 text-zinc-600 hover:text-white opacity-0 group-hover:opacity-100 transition-all">
                    <Edit className="w-3 h-3" />
                  </button>
                  <button onClick={() => handleDeleteSection(s)} className="p-1 text-zinc-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Staff list */}
      {staff.length === 0 && !showCreate ? (
        <div className="text-center py-12 bg-zinc-900/40 border border-white/5">
          <p className="text-zinc-600 text-sm">No staff members yet.</p>
          <button onClick={() => setShowCreate(true)} className="mt-3 text-zinc-400 hover:text-white text-xs">Add your first member</button>
        </div>
      ) : (
        <div className="space-y-2">
          {staff.map((m) => (
            <div key={m.id}>
              {editingId === m.id ? (
                <StaffForm initial={m} sections={sections} onSave={(form) => handleUpdate(m.id, form)} onCancel={() => setEditingId(null)} />
              ) : (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  className={`bg-zinc-900/60 border border-white/10 p-4 flex items-center gap-4 group hover:border-white/20 transition-colors ${!m.is_visible ? "opacity-50" : ""}`}
                  data-testid={`staff-card-${m.id}`}>
                  <div className="w-12 h-12 bg-black/40 shrink-0 overflow-hidden">
                    {m.photo_url ? (
                      <img src={m.photo_url} alt={m.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-zinc-600 text-lg font-bold">{m.name?.[0]}</div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-white text-sm font-bold truncate">{m.name}</h4>
                    <p className="text-zinc-500 text-xs truncate">{m.role}</p>
                    <div className="flex items-center gap-3 text-[10px] text-zinc-600 mt-0.5">
                      {m.section_id && <span className="bg-zinc-800 px-1.5 py-px">{getSectionName(m.section_id)}</span>}
                      {m.years_with_company != null && <span>{m.years_with_company}yr{m.years_with_company !== 1 ? "s" : ""}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => toggleVisibility(m)} className="p-1.5 text-zinc-500 hover:text-white" title={m.is_visible ? "Hide" : "Show"}>
                      {m.is_visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                    </button>
                    <button onClick={() => { setEditingId(m.id); setShowCreate(false); }} className="p-1.5 text-zinc-500 hover:text-white" title="Edit" data-testid={`edit-staff-${m.id}`}>
                      <Edit className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => handleDelete(m)} className="p-1.5 text-zinc-500 hover:text-red-400" title="Delete">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </motion.div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StaffTab;
